import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  usersApi, USER_ROLES, ROLE_LABEL, ROLE_FILTERS, ACTIVE_FILTERS,
} from '../../services/usersApi';
import { getToken, getUser } from '../auth';
import '../styles/Membres.css';
import './admin-form.css';

const EMPTY_FORM = {
  prenom: '', nom: '', email: '',
  role: 'membre', phone: '',
  password: '',
  active: true,
  adminNote: '',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function initialsOf(prenom, nom) {
  const p = (prenom || '').trim()[0] || '';
  const n = (nom || '').trim()[0] || '';
  return (p + n).toUpperCase() || '?';
}

const ROLE_PILL_CLASS = {
  admin:        'sunset',
  moderateur:   'gold',
  contributeur: 'ocean',
  membre:       '',
};

const Membres = () => {
  const token   = getToken();
  const me      = getUser();
  const myId    = me?.id || null;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const [filterRole,   setFilterRole]   = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const [query,        setQuery]        = useState('');

  const [open,       setOpen]       = useState(false);
  const [editingId,  setEditingId]  = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState(null);

  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd,    setResetPwd]    = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [resetError,  setResetError]  = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await usersApi.adminList(token, {
        role:   filterRole   !== 'all' ? filterRole   : undefined,
        active: filterActive !== 'all' ? filterActive : undefined,
        q: query || undefined,
        limit: 200,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filterRole, filterActive, query]);

  useEffect(() => { load(); }, [load]);

  const kpi = useMemo(() => {
    const c = { total: items.length, admins: 0, mods: 0, actifs: 0, suspendus: 0 };
    for (const u of items) {
      if (u.role === 'admin')      c.admins++;
      if (u.role === 'moderateur') c.mods++;
      if (u.active)                c.actifs++;
      else                         c.suspendus++;
    }
    return c;
  }, [items]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSaveError(null);
    setOpen(true);
  };

  const openEdit = async (id) => {
    setEditingId(id);
    setSaveError(null);
    try {
      const { user } = await usersApi.adminGet(token, id);
      setForm({
        prenom:    user.prenom,
        nom:       user.nom,
        email:     user.email,
        role:      user.role,
        phone:     user.phone || '',
        password:  '',
        active:    user.active,
        adminNote: user.adminNote || '',
      });
      setOpen(true);
    } catch (err) {
      alert('Impossible de charger le membre : ' + err.message);
    }
  };

  const close = useCallback(() => {
    setOpen(false); setEditingId(null); setForm(EMPTY_FORM); setSaveError(null);
  }, []);

  useEffect(() => {
    if (!open && !resetTarget) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (resetTarget) closeReset();
      else if (open)   close();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [open, resetTarget, close]);

  const isEditingSelf = editingId && editingId === myId;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      if (editingId) {
        const payload = {
          prenom: form.prenom,
          nom: form.nom,
          email: form.email,
          phone: form.phone,
          role: form.role,
          active: form.active,
          adminNote: form.adminNote,
        };
        await usersApi.adminUpdate(token, editingId, payload);
      } else {
        if (!form.password || form.password.length < 8) {
          throw new Error('Mot de passe initial : 8 caractères minimum');
        }
        const payload = {
          prenom: form.prenom,
          nom: form.nom,
          email: form.email,
          phone: form.phone,
          role: form.role,
          password: form.password,
          active: form.active,
          adminNote: form.adminNote,
        };
        await usersApi.adminCreate(token, payload);
      }
      close();
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (u) => {
    if (u.id === myId) {
      alert('Vous ne pouvez pas supprimer votre propre compte.');
      return;
    }
    if (!window.confirm(`Supprimer définitivement « ${u.fullName} » (${u.email}) ?`)) return;
    try {
      await usersApi.adminDelete(token, u.id);
      await load();
    } catch (err) {
      alert('Suppression impossible : ' + err.message);
    }
  };

  const onToggleActive = async (u) => {
    if (u.id === myId && u.active) {
      alert('Vous ne pouvez pas désactiver votre propre compte.');
      return;
    }
    try {
      await usersApi.adminUpdate(token, u.id, { active: !u.active });
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  const onPromote = async (u) => {
    if (u.role === 'admin') return;
    if (!window.confirm(`Promouvoir « ${u.fullName} » au rôle Admin ?`)) return;
    try {
      await usersApi.adminUpdate(token, u.id, { role: 'admin' });
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  const openReset = (u) => {
    setResetTarget(u);
    setResetPwd('');
    setResetError(null);
  };

  const closeReset = () => {
    setResetTarget(null);
    setResetPwd('');
    setResetError(null);
  };

  const onResetSubmit = async (e) => {
    e.preventDefault();
    if (!resetTarget) return;
    setResetSaving(true);
    setResetError(null);
    try {
      if (!resetPwd || resetPwd.length < 8) {
        throw new Error('Nouveau mot de passe : 8 caractères minimum');
      }
      await usersApi.adminResetPassword(token, resetTarget.id, resetPwd);
      closeReset();
      alert('Mot de passe réinitialisé.');
    } catch (err) {
      setResetError(err.message);
    } finally {
      setResetSaving(false);
    }
  };

  if (!token) {
    return (
      <div className="content" style={{ padding: 40 }}>
        <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', color: 'var(--char-500)' }}>
          Session expirée. Reconnectez-vous.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="topbar">
        <div className="breadcrumb">
          <span>Cambérène</span><span className="sep">/</span>
          <span>Admin</span><span className="sep">/</span>
          <b>Membres</b>
        </div>
        <div className="search">
          <span className="ic" />
          <input
            type="text"
            placeholder="Chercher par nom, email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd>⌘ K</kbd>
        </div>
        <div className="top-actions">
          <button className="icon-btn" title="Rafraîchir" onClick={load} aria-label="Rafraîchir">↻</button>
          <button className="top-cta" onClick={openCreate}>
            <span className="plus" /> Nouveau membre
          </button>
        </div>
      </div>

      <div className="content">
        <div className="page-head">
          <div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
              Communauté · {total} membre{total > 1 ? 's' : ''}
            </span>
            <h1>Le village,<br /><span style={{ fontStyle: 'italic' }}>en personnes.</span></h1>
            <p className="sub">Gérer les comptes, attribuer les rôles, suspendre ou réinitialiser les accès.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="pill-btn"
              style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}
            >
              {ROLE_FILTERS.map((r) => <option key={r.id} value={r.id}>{r.lbl}</option>)}
            </select>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="pill-btn"
              style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}
            >
              {ACTIVE_FILTERS.map((a) => <option key={a.id} value={a.id}>{a.lbl}</option>)}
            </select>
          </div>
        </div>

        {/* KPI bandeau */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
          {[
            { lbl: 'Admins',        val: kpi.admins,    color: '#E76F51' },
            { lbl: 'Modérateurs',   val: kpi.mods,      color: '#C9A961' },
            { lbl: 'Actifs',        val: kpi.actifs,    color: '#34A853' },
            { lbl: 'Suspendus',     val: kpi.suspendus, color: '#E63946' },
          ].map((k) => (
            <div key={k.lbl} className="kpi">
              <span className="lab">
                <span className="dot" style={{ background: k.color }} />
                {k.lbl}
              </span>
              <b>{k.val}</b>
            </div>
          ))}
        </div>

        {error && (
          <div className="bulkbar on" style={{ background: 'rgba(230,57,70,.12)', color: '#B72333' }}>
            <span>⚠</span><span>Erreur : {error}</span>
          </div>
        )}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 56 }} />
                <th>Nom / Email</th>
                <th>Rôle</th>
                <th>Téléphone</th>
                <th>Statut</th>
                <th>Dernière connexion</th>
                <th>Créé</th>
                <th style={{ width: 160 }} />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>Chargement…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)' }}>
                  Aucun membre. Cliquez « Nouveau membre » pour démarrer.
                </td></tr>
              )}
              {items.map((u) => {
                const self = u.id === myId;
                return (
                  <tr key={u.id} onClick={() => openEdit(u.id)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div
                        style={{
                          width: 40, height: 40, borderRadius: '50%',
                          background: 'var(--bone-2)',
                          border: '1px solid var(--line)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontFamily: 'var(--serif)', fontSize: 15, color: 'var(--char-700)',
                          letterSpacing: '.02em',
                        }}
                      >
                        {initialsOf(u.prenom, u.nom)}
                      </div>
                    </td>
                    <td>
                      <b style={{ fontWeight: 500, fontSize: 14 }}>
                        {u.fullName}
                        {self && <span style={{ marginLeft: 6, fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.14em', color: 'var(--sunset)' }}>· VOUS</span>}
                      </b>
                      <small style={{ display: 'block', marginTop: 3, fontSize: 12, color: 'var(--char-500)' }}>{u.email}</small>
                    </td>
                    <td>
                      <span className={`cat-pill ${ROLE_PILL_CLASS[u.role] || ''}`}>
                        {ROLE_LABEL[u.role] || u.role}
                      </span>
                    </td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-700)' }}>
                      {u.phone || '—'}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <span
                        className={`status-pill ${u.active ? 'live' : 'err'}`}
                        style={{ cursor: self && u.active ? 'not-allowed' : 'pointer' }}
                        onClick={() => onToggleActive(u)}
                        title={u.active ? (self ? 'Vous ne pouvez pas vous désactiver' : 'Cliquer pour suspendre') : 'Cliquer pour réactiver'}
                      >
                        {u.active ? 'Actif' : 'Suspendu'}
                      </span>
                    </td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>
                      {fmtDate(u.lastLoginAt)}
                    </td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>
                      {fmtDate(u.createdAt)}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="row-actions" style={{ opacity: 1 }}>
                        {u.role !== 'admin' && (
                          <button title="Promouvoir admin" onClick={() => onPromote(u)} style={{ color: '#A93D29' }}>★</button>
                        )}
                        <button title="Réinitialiser mot de passe" onClick={() => openReset(u)}>⟲</button>
                        <button title="Éditer" onClick={() => openEdit(u.id)}>✎</button>
                        <button
                          title={self ? 'Vous ne pouvez pas vous supprimer' : 'Supprimer'}
                          className="danger"
                          onClick={() => onDelete(u)}
                          disabled={self}
                          style={self ? { opacity: .4, cursor: 'not-allowed' } : {}}
                        >✕</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="table-foot">
            <span>{total} membre{total > 1 ? 's' : ''}</span>
            <span>{items.length} affiché{items.length > 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* ============ MODALE CRÉATION / ÉDITION ============ */}
      {open && (
        <div className="admin-modal-bg" onClick={close}>
          <div className="admin-modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="admin-modal-head">
              <div>
                <span className="admin-modal-eyebrow">
                  {editingId ? 'Édition' : 'Nouveau membre'}
                </span>
                <h2 className="admin-modal-title">
                  {editingId ? `${form.prenom} ${form.nom}` : 'Ajouter un membre'}
                </h2>
              </div>
              <button onClick={close} className="admin-modal-close" aria-label="Fermer">✕</button>
            </div>

            <form className="admin-form" onSubmit={onSubmit}>
              <div className="admin-form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Prénom *</span>
                  <input
                    type="text"
                    value={form.prenom}
                    onChange={(e) => setForm((f) => ({ ...f, prenom: e.target.value }))}
                    required
                  />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Nom *</span>
                  <input
                    type="text"
                    value={form.nom}
                    onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))}
                    required
                  />
                </label>
              </div>

              <div className="admin-form-row" style={{ gridTemplateColumns: '1.4fr 1fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Email *</span>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    required
                  />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Téléphone</span>
                  <input
                    type="text"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="+221 …"
                  />
                </label>
              </div>

              <div className="admin-form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">
                    Rôle{isEditingSelf && <em style={{ marginLeft: 6, fontStyle: 'normal', color: 'var(--sunset)' }}>(verrouillé sur soi)</em>}
                  </span>
                  <select
                    value={form.role}
                    onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                    disabled={isEditingSelf}
                  >
                    {USER_ROLES.map((r) => <option key={r.id} value={r.id}>{r.lbl}</option>)}
                  </select>
                </label>
                {!editingId && (
                  <label className="admin-field">
                    <span className="admin-field-label">Mot de passe initial *</span>
                    <input
                      type="text"
                      value={form.password}
                      onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                      placeholder="8 caractères min."
                      required
                    />
                  </label>
                )}
              </div>

              <label className="admin-publish-toggle">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  disabled={isEditingSelf && form.active}
                />
                <span>
                  {form.active ? 'Compte actif' : 'Compte suspendu (login bloqué)'}
                  {isEditingSelf && form.active && (
                    <em style={{ marginLeft: 8, fontStyle: 'normal', color: 'var(--sunset)', fontSize: 11 }}>
                      verrouillé sur soi
                    </em>
                  )}
                </span>
              </label>

              <label className="admin-field">
                <span className="admin-field-label">Note interne (admin uniquement, jamais publique)</span>
                <textarea
                  rows={2}
                  value={form.adminNote}
                  onChange={(e) => setForm((f) => ({ ...f, adminNote: e.target.value }))}
                  placeholder="Ex : référent diaspora, contact prioritaire, etc."
                />
              </label>

              {saveError && <div className="admin-form-error">{saveError}</div>}

              <div className="admin-form-foot">
                <button type="button" onClick={close} className="admin-btn">Annuler</button>
                <button type="submit" disabled={saving} className="admin-btn admin-btn-primary">
                  {saving ? 'Enregistrement…' : (editingId ? 'Enregistrer' : 'Créer le membre')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============ MODALE RESET PASSWORD ============ */}
      {resetTarget && (
        <div className="admin-modal-bg" onClick={closeReset}>
          <div className="admin-modal" onClick={(ev) => ev.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="admin-modal-head">
              <div>
                <span className="admin-modal-eyebrow">Réinitialisation</span>
                <h2 className="admin-modal-title">Nouveau mot de passe</h2>
              </div>
              <button onClick={closeReset} className="admin-modal-close" aria-label="Fermer">✕</button>
            </div>
            <form className="admin-form" onSubmit={onResetSubmit}>
              <p style={{ margin: '0 0 12px', fontSize: 13.5, color: 'var(--char-700)' }}>
                Définir un nouveau mot de passe pour <b>{resetTarget.fullName}</b> ({resetTarget.email}).
                Communiquez-le ensuite directement à la personne.
              </p>
              <label className="admin-field">
                <span className="admin-field-label">Nouveau mot de passe *</span>
                <input
                  type="text"
                  value={resetPwd}
                  onChange={(e) => setResetPwd(e.target.value)}
                  placeholder="8 caractères min."
                  required
                  autoFocus
                />
              </label>
              {resetError && <div className="admin-form-error">{resetError}</div>}
              <div className="admin-form-foot">
                <button type="button" onClick={closeReset} className="admin-btn">Annuler</button>
                <button type="submit" disabled={resetSaving} className="admin-btn admin-btn-primary">
                  {resetSaving ? 'Enregistrement…' : 'Réinitialiser'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Membres;
